/**
 * 触摸优化Hook
 * Feature: mobile-responsive-framework, Task 4: 实现触摸优化系统
 * 需求: 3.1, 3.2, 3.3, 3.4
 */
import { useState, useEffect, useMemo } from 'react';
import { useResponsiveConfig } from './useResponsiveConfig';
import {
  TOUCH_CONFIG,
  COMPONENT_TOUCH_CONFIGS,
  isTouchDevice,
  isCoarsePointer,
  supportsHover,
  getComponentTouchConfig,
  getSafeTouchTargetSize,
  getSafeButtonSpacing,
  getSafeListItemSpacing
} from '../constants/touch';

// 触摸配置接口
export interface TouchConfig {
  // 触摸设备状态
  isTouchDevice: boolean;
  isCoarsePointer: boolean;
  supportsHover: boolean;
  
  // 触摸目标配置
  minTouchTarget: number;   // 最小触摸目标 44px
  touchPadding: number;     // 触摸区域内边距
  
  // 间距配置
  itemSpacing: number;      // 列表项间距
  buttonSpacing: number;    // 按钮间距
  
  // 组件配置获取函数
  getButtonProps: () => ButtonTouchProps;
  getInputProps: () => InputTouchProps;
  getListItemProps: () => ListItemTouchProps;
  getSelectProps: () => SelectTouchProps;
  getCheckboxProps: () => CheckboxTouchProps;
}

// 按钮触摸属性
export interface ButtonTouchProps {
  size: 'small' | 'middle' | 'large';
  style: React.CSSProperties;
}

// 输入框触摸属性
export interface InputTouchProps {
  size: 'small' | 'middle' | 'large';
  style: React.CSSProperties;
}

// 列表项触摸属性
export interface ListItemTouchProps {
  style: React.CSSProperties;
}

// 选择器触摸属性
export interface SelectTouchProps {
  size: 'small' | 'middle' | 'large';
  style: React.CSSProperties;
}

// 复选框触摸属性
export interface CheckboxTouchProps {
  style: React.CSSProperties;
}

/**
 * 触摸优化Hook
 * 提供触摸设备检测和触摸优化配置
 */
export function useTouchOptimization(): TouchConfig {
  const { isMobile, isTablet } = useResponsiveConfig();
  
  // 触摸设备状态
  const [touchState, setTouchState] = useState({
    isTouchDevice: false,
    isCoarsePointer: false,
    supportsHover: true
  });
  
  // 初始化触摸设备检测
  useEffect(() => {
    setTouchState({
      isTouchDevice: isTouchDevice(),
      isCoarsePointer: isCoarsePointer(),
      supportsHover: supportsHover()
    });
  }, []);
  
  // 是否需要触摸优化
  const needsTouchOptimization = useMemo(() => {
    return touchState.isTouchDevice || touchState.isCoarsePointer || isMobile || isTablet;
  }, [touchState, isMobile, isTablet]);
  
  // 最小触摸目标
  const minTouchTarget = useMemo(() => {
    return needsTouchOptimization ? TOUCH_CONFIG.minTouchTarget : 32;
  }, [needsTouchOptimization]);
  
  // 触摸内边距
  const touchPadding = useMemo(() => {
    return needsTouchOptimization ? TOUCH_CONFIG.touchPadding : 4;
  }, [needsTouchOptimization]);
  
  // 列表项间距
  const itemSpacing = useMemo(() => {
    return getSafeListItemSpacing(4, needsTouchOptimization);
  }, [needsTouchOptimization]);
  
  // 按钮间距
  const buttonSpacing = useMemo(() => {
    return getSafeButtonSpacing(8, needsTouchOptimization);
  }, [needsTouchOptimization]);
  
  // 获取按钮触摸属性
  const getButtonProps = useMemo(() => {
    return (): ButtonTouchProps => {
      const config = getComponentTouchConfig('button', needsTouchOptimization);
      return {
        size: needsTouchOptimization ? 'large' : 'middle',
        style: {
          minHeight: config.minHeight,
          minWidth: config.minWidth,
          padding: config.padding
        }
      };
    };
  }, [needsTouchOptimization]);
  
  // 获取输入框触摸属性
  const getInputProps = useMemo(() => {
    return (): InputTouchProps => {
      const config = getComponentTouchConfig('input', needsTouchOptimization);
      return {
        size: needsTouchOptimization ? 'large' : 'middle',
        style: {
          minHeight: config.minHeight,
          padding: config.padding
        }
      };
    };
  }, [needsTouchOptimization]);
  
  // 获取列表项触摸属性
  const getListItemProps = useMemo(() => {
    return (): ListItemTouchProps => {
      const config = getComponentTouchConfig('listItem', needsTouchOptimization);
      return {
        style: {
          minHeight: config.minHeight,
          padding: config.padding,
          marginBottom: itemSpacing
        }
      };
    };
  }, [needsTouchOptimization, itemSpacing]);
  
  // 获取选择器触摸属性
  const getSelectProps = useMemo(() => {
    return (): SelectTouchProps => {
      const config = getComponentTouchConfig('select', needsTouchOptimization);
      return {
        size: needsTouchOptimization ? 'large' : 'middle',
        style: {
          minHeight: config.minHeight
        }
      };
    };
  }, [needsTouchOptimization]);
  
  // 获取复选框触摸属性
  const getCheckboxProps = useMemo(() => {
    return (): CheckboxTouchProps => {
      const config = getComponentTouchConfig('checkbox', needsTouchOptimization);
      return {
        style: {
          minHeight: config.minHeight,
          minWidth: config.minWidth,
          padding: config.padding
        }
      };
    };
  }, [needsTouchOptimization]);
  
  return {
    isTouchDevice: touchState.isTouchDevice,
    isCoarsePointer: touchState.isCoarsePointer,
    supportsHover: touchState.supportsHover,
    minTouchTarget,
    touchPadding,
    itemSpacing,
    buttonSpacing,
    getButtonProps,
    getInputProps,
    getListItemProps,
    getSelectProps,
    getCheckboxProps
  };
}

export default useTouchOptimization;
